import { router, useNavigation } from 'expo-router';
import { useEffect, useLayoutEffect, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { useGetMeMutation, useNpoOnboarding, useOnBoarding } from '@/api/hooks/meHooks';
import GoBackButton from '@/components/buttons/GoBackButton';
import PreferencesForm from '@/components/forms/PreferencesForm';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import { InterestsData } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';

const PreferencesScreen = () => {
  const colors = useColor();
  const { state: authState, setPreferences, setSecularPreferences, setChurchPreferences } = useAuth();
  const type = authState.signUpData?.loginEmail?.type;
  const isNotCharity = type !== 'CHARITIES';
  const navigation = useNavigation();
  const { mutateAsync: onBoardingUpdate, isPending } = useOnBoarding();
  const { mutateAsync: npoOnBoardingUpdate, isPending: isNpoUpdatePending } = useNpoOnboarding(type ?? 'CHARITIES');
  const { mutateAsync: getMe } = useGetMeMutation();

  const { showSuccess, showError } = useEndFlowModal();

  //Locales
  const title = i18n.t('settings.preferences.title');
  const success = i18n.t('end_flow.success_title');
  const success_message = i18n.t('settings.preferences.success_message');
  const failure = i18n.t('end_flow.error_title');
  const failure_message = i18n.t('settings.preferences.failure_message');
  const descriptionText = i18n.t('sign_in.individual.interests.description');

  const [defaultValues, setDefaultValues] = useState<InterestsData>({
    denominations: [],
    religions: [],
    programs: [],
    checkbox_selection: undefined
  });

  useEffect(() => {
    if (type === 'CHARITIES') {
      setDefaultValues({
        denominations: [],
        religions: [],
        programs:
          authState.user?.onBoardingPreferenceSecular?.at(0)?.programTypes.map((item) => item.programTypeId ?? '-') ??
          [],
        checkbox_selection: undefined
      });
    } else if (type === 'CHURCH') {
      setDefaultValues({
        denominations:
          authState.user?.onBoardingPreferenceChurch
            ?.at(0)
            ?.denominationTypes.map((item) => item.denominationTypeId ?? '-') ?? [],
        religions: authState.user?.onBoardingPreferenceChurch?.at(0)?.religionTypes.map((item) => item.id ?? '-') ?? [],
        programs:
          authState.user?.onBoardingPreferenceChurch?.at(0)?.programTypes.map((item) => item.programTypeId ?? '-') ??
          [],
        checkbox_selection: undefined
      });
    } else {
      setDefaultValues({
        denominations:
          authState.user?.onBoardingPreference?.denominationTypes.map((item) => item.denominationTypeId ?? '-') ?? [],
        religions: authState.user?.onBoardingPreference?.religionTypes.map((item) => item.id ?? '-') ?? [],
        programs: authState.user?.onBoardingPreference?.programTypes.map((item) => item.programTypeId ?? '-') ?? [],
        checkbox_selection: undefined
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={title} />,
      headerLeft: () => <GoBackButton />
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  const errorHandler = {
    onSuccess: () => {
      getMe(
        {
          firebaseId: authState.user?.user.firebaseId!,
          onBoardingType: type === 'CHARITIES' || type === 'CHURCH' ? 'npo' : 'individual'
        },
        {
          onSuccess: (data) => {
            if (type === 'CHARITIES') {
              setSecularPreferences({
                programTypes: data.onBoardingPreferenceSecular?.at(0)?.programTypes ?? []
              });
            } else if (type === 'CHURCH') {
              setChurchPreferences({
                programTypes: data.onBoardingPreferenceChurch?.at(0)?.programTypes ?? [],
                denominationTypes: data.onBoardingPreferenceChurch?.at(0)?.denominationTypes ?? [],
                religionTypes: data.onBoardingPreferenceChurch?.at(0)?.religionTypes ?? []
              });
            } else {
              setPreferences({
                programTypes: data.onBoardingPreference?.programTypes ?? [],
                denominationTypes: data.onBoardingPreference?.denominationTypes ?? [],
                religionTypes: data.onBoardingPreference?.religionTypes ?? []
              });
            }
            showSuccess({
              title: success,
              message: success_message
            });
            router.back();
          }
        }
      );
    },
    onError: () => {
      showError({
        title: failure,
        message: failure_message
      });
    }
  };

  const onDone = async (data: InterestsData) => {
    if (type === 'CHARITIES' || type === 'CHURCH') {
      await npoOnBoardingUpdate(
        {
          preferenceId: authState.preferenceId!,
          userInterestOnReligion: data.religions,
          userInterestOnDenomination: data.denominations,
          userInterestOnProgram: data.programs
        },
        errorHandler
      );
    } else {
      await onBoardingUpdate(
        {
          onBoardingId: authState.user?.onBoardingPreference?.id!,
          userInterestOnReligion: data.religions,
          userInterestOnDenomination: data.denominations,
          userInterestOnProgram: data.programs
        },
        errorHandler
      );
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false}>
      <Text style={[styles.descriptionText, { color: colors.titleText }]}>{descriptionText}</Text>
      <PreferencesForm
        defaultValues={defaultValues}
        onDone={onDone}
        isPending={isPending || isNpoUpdatePending}
        maxPrograms={type !== undefined ? 5 : undefined}
        showDenominations={isNotCharity}
        showReligions={isNotCharity}
        submitLabel={i18n.t('mix.done')}
      />
    </ScrollView>
  );
};

export default PreferencesScreen;

const styles = StyleSheet.create({
  scrollContainer: {
    paddingHorizontal: 16,
    flexGrow: 1,
    paddingTop: 18
  },
  titleText: {
    fontSize: 22,
    marginBottom: 25,
    textAlign: 'center',
    marginTop: 18
  },
  descriptionText: {
    fontSize: 14,
    marginBottom: 25
  }
});
